import { isEqual } from 'lodash';

import { ModuleContainerUnion, QueryItem } from 'data/generated/graphql';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

function hasKey(fieldValue: unknown, key: string): boolean {
  return fieldValue !== null && typeof fieldValue === 'object' && (fieldValue as Record<string, unknown>).key === key;
}

function isUpstreamOriginFilter(item: unknown): boolean {
  if (!item || typeof item !== 'object') return false;

  return (
    ['term', 'terms']
      .map((field) => (item as Record<string, unknown>)[field])
      // term / terms filter targeting UpstreamOriginId
      .some((fieldValue) => hasKey(fieldValue, 'UpstreamOriginId'))
  );
}

// Normalize queries: remove UpstreamOriginId, lowercase strings, and sort arrays for order-independent comparison
const normalizeQueryForComparison = (query: unknown): unknown => {
  // apply lowercase
  if (query === null || typeof query !== 'object') {
    return typeof query === 'string' ? query.toLowerCase() : query;
  }

  if (Array.isArray(query)) {
    const filtered = query.filter((item) => !isUpstreamOriginFilter(item)).map(normalizeQueryForComparison);

    return filtered.length === 0 ? undefined : filtered;
  }

  // Handle objects - normalize nested values and sort arrays
  const normalized = Object.fromEntries(
    Object.entries(query)
      .map(([key, value]): [string, unknown] => {
        const normalizedValue = normalizeQueryForComparison(value);
        // Sort arrays to ensure order-independent comparison (e.g., terms.value arrays)
        if (Array.isArray(normalizedValue)) {
          // Sort array elements (handles strings, numbers, etc.)
          const sortedArray = (normalizedValue as unknown[]).slice().sort((a, b) => String(a).localeCompare(String(b)));
          return [key, sortedArray];
        }
        return [key, normalizedValue];
      })
      .filter(([, value]) => value !== undefined)
  );

  return normalized;
};

export const areQueriesEqual = (query1: string | null, query2: string | null): boolean => {
  if (!query1 || !query2) {
    return false;
  }

  const parsed1 = JSON.parse(query1) as Record<string, unknown>;
  const parsed2 = JSON.parse(query2) as Record<string, unknown>;

  const normalized1 = normalizeQueryForComparison(parsed1.query) as Record<string, unknown>;
  const normalized2 = normalizeQueryForComparison(parsed2.query) as Record<string, unknown>;

  // Use lodash isEqual for deep object comparison (order-independent, handles nested objects)
  return isEqual(normalized1, normalized2);
};

export const calculateOverfetchValue = (totalOccurrences: number): number | null => {
  // apply overfetch if this is the 3 or more occurrence
  if (totalOccurrences < 3) {
    return null;
  }

  // calculate overfetchBy based on occurrence number
  // 3 occurrence = 40, 4 occurrence = 50, 5 occurrence = 60, etc.
  const baseOverfetch = 40;
  const increment = 10;
  return baseOverfetch + (totalOccurrences - 3) * increment;
};

// Helper function to check if a module contains a matching query (ignoring excludeQueryId)
export const moduleContainsMatchingQuery = (
  module: ModuleContainerUnion,
  query: Record<string, unknown>,
  excludeQueryId?: string
): boolean => {
  const queryJson = JSON.stringify(query);
  const queryItems = module.collection.filter((item) => item.type === EntityType.Query) as QueryItem[];

  return queryItems.some((queryItem) => {
    if (excludeQueryId && queryItem.attributes.id === excludeQueryId) {
      return false;
    }

    const existingQueryJson = JSON.stringify(queryItem.attributes.query);
    return areQueriesEqual(queryJson, existingQueryJson);
  });
};
